import {
  characteristicPolynomial,
  type Matrix,
  nullSpace,
  R_ONE,
  R_ZERO,
} from "@/lib/tool-kit/linear-algebra/matrix";
import {
  type Complex,
  numericEigenvalues,
  numericEigenvector,
  REAL_TOLERANCE,
} from "@/lib/tool-kit/linear-algebra/numeric-eigen";
import {
  divQ,
  isZeroQ,
  mulQ,
  negQ,
  Q_ONE,
  Q_ZERO,
  type QuadNumber,
  quadFromRational,
  subQ,
} from "@/lib/tool-kit/linear-algebra/quad-number";
import { absBig, BIG_ONE, BIG_ZERO, gcdBig } from "@/lib/tool-kit/math/bigint";
import { factorize, listDivisors } from "@/lib/tool-kit/math/primes";
import { solveQuadratic } from "@/lib/tool-kit/math/quadratic";
import {
  addR,
  commonDenominator,
  divR,
  fromBigInt,
  isZeroR,
  makeRational,
  mulR,
  negR,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Eigenvalues and eigenvectors of a rational matrix.
 *
 * The characteristic polynomial det(λI − A) is computed exactly. Rational roots are
 * found with the rational root theorem and divided out; if what remains has degree two
 * it is solved with the quadratic formula, so every eigenvalue is an exact fraction,
 * radical or complex number (a + b√d). Anything else — an irreducible cubic or higher —
 * falls back to the numeric QR algorithm.
 */

/* ------------------------------------------------------------ polynomials */

/** Coefficients lowest power first. */
export type Poly = Rational[];

export function evalPoly(p: Poly, x: Rational): Rational {
  let result = R_ZERO;
  for (let i = p.length - 1; i >= 0; i -= 1) {
    result = addR(mulR(result, x), p[i]);
  }
  return result;
}

/** p(λ) ÷ (λ − root), assuming root is a root. */
export function deflate(p: Poly, root: Rational): Poly {
  const n = p.length - 1;
  const quotient: Poly = Array.from({ length: n }, () => R_ZERO);
  let carry = R_ZERO;
  for (let i = n; i >= 1; i -= 1) {
    carry = addR(p[i], mulR(carry, root));
    quotient[i - 1] = carry;
  }
  return quotient;
}

export interface RationalRoot {
  root: Rational;
  multiplicity: number;
}

export interface Factorization {
  roots: RationalRoot[];
  /** What is left after every rational root is divided out (monic), lowest power first. */
  remainder: Poly;
  /** False when the root search had to give up (huge coefficients). */
  searched: boolean;
}

const MAX_CANDIDATES = 40_000;

function integerScale(p: Poly): bigint[] {
  const lcd = commonDenominator(p);
  const ints = p.map((c) => (c.num * lcd) / c.den);
  const common = ints.reduce((g, x) => gcdBig(g, x), BIG_ZERO);
  return common === BIG_ZERO ? ints : ints.map((x) => x / common);
}

function divisorsOf(n: bigint): bigint[] | null {
  const { factors, complete } = factorize(absBig(n));
  if (!complete) {
    return null;
  }
  return listDivisors(factors, MAX_CANDIDATES);
}

function candidateRoots(p: Poly): Rational[] | null {
  const ints = integerScale(p);
  const top = divisorsOf(ints.at(-1) ?? BIG_ONE);
  const bottom = divisorsOf(ints[0]);
  if (
    top === null ||
    bottom === null ||
    top.length * bottom.length > MAX_CANDIDATES
  ) {
    return null;
  }
  const seen = new Set<string>();
  const out: Rational[] = [];
  for (const num of bottom) {
    for (const den of top) {
      for (const sign of [BIG_ONE, -BIG_ONE]) {
        const r = makeRational(sign * num, den);
        const key = `${r.num}/${r.den}`;
        if (!seen.has(key)) {
          seen.add(key);
          out.push(r);
        }
      }
    }
  }
  return out;
}

function sortRoots(roots: RationalRoot[]): RationalRoot[] {
  return [...roots].sort((a, b) => toNumberR(a.root) - toNumberR(b.root));
}

/** Divide out every rational root. */
export function factorRationalRoots(poly: Poly): Factorization {
  let p = poly;
  const roots: RationalRoot[] = [];
  let zeros = 0;
  while (p.length > 1 && isZeroR(p[0])) {
    p = p.slice(1);
    zeros += 1;
  }
  if (zeros > 0) {
    roots.push({ root: R_ZERO, multiplicity: zeros });
  }
  if (p.length <= 2) {
    if (p.length === 2) {
      roots.push({ root: negR(divR(p[0], p[1])), multiplicity: 1 });
      p = [R_ONE];
    }
    return { roots: sortRoots(roots), remainder: p, searched: true };
  }
  const candidates = candidateRoots(p);
  if (candidates === null) {
    return { roots: sortRoots(roots), remainder: p, searched: false };
  }
  for (const candidate of candidates) {
    let multiplicity = 0;
    while (p.length > 1 && isZeroR(evalPoly(p, candidate))) {
      p = deflate(p, candidate);
      multiplicity += 1;
    }
    if (multiplicity > 0) {
      roots.push({ root: candidate, multiplicity });
    }
  }
  return { roots: sortRoots(roots), remainder: p, searched: true };
}

/* ----------------------------------------------------- vectors over Q(√d) */

/** Clear denominators, divide out the common factor, make the first entry positive. */
function toIntegerVector(v: Rational[]): Rational[] {
  const lcd = commonDenominator(v);
  const ints = v.map((x) => (x.num * lcd) / x.den);
  const common = ints.reduce((g, x) => gcdBig(g, x), BIG_ZERO);
  const first = ints.find((x) => x !== BIG_ZERO) ?? BIG_ONE;
  const sign = first < BIG_ZERO ? -BIG_ONE : BIG_ONE;
  return ints.map((x) =>
    fromBigInt(common === BIG_ZERO ? x : (sign * x) / common)
  );
}

function shiftMatrix(a: Matrix, lambda: QuadNumber): QuadNumber[][] {
  return a.map((row, i) =>
    row.map((value, j) => {
      const entry = quadFromRational(value);
      return i === j ? subQ(entry, lambda) : entry;
    })
  );
}

/** Null space over Q(√d) by Gauss–Jordan; one vector per free column. */
export function nullSpaceQuad(m: QuadNumber[][]): QuadNumber[][] {
  const rows = m.length;
  const cols = m[0].length;
  const work = m.map((row) => row.slice());
  const pivotCols: number[] = [];
  let pivotRow = 0;
  for (let col = 0; col < cols && pivotRow < rows; col += 1) {
    const found = work.findIndex(
      (row, r) => r >= pivotRow && !isZeroQ(row[col])
    );
    if (found === -1) {
      continue;
    }
    [work[found], work[pivotRow]] = [work[pivotRow], work[found]];
    const pivot = work[pivotRow][col];
    work[pivotRow] = work[pivotRow].map((x) => divQ(x, pivot));
    for (let r = 0; r < rows; r += 1) {
      if (r === pivotRow || isZeroQ(work[r][col])) {
        continue;
      }
      const factor = work[r][col];
      work[r] = work[r].map((x, c) => subQ(x, mulQ(factor, work[pivotRow][c])));
    }
    pivotCols.push(col);
    pivotRow += 1;
  }
  const free = Array.from({ length: cols }, (_, c) => c).filter(
    (c) => !pivotCols.includes(c)
  );
  return free.map((freeCol) => {
    const vector: QuadNumber[] = Array.from({ length: cols }, () => Q_ZERO);
    vector[freeCol] = Q_ONE;
    pivotCols.forEach((pivotCol, r) => {
      vector[pivotCol] = negQ(work[r][freeCol]);
    });
    return vector;
  });
}

/* ------------------------------------------------------------ the solution */

export interface ExactEigenvalue {
  value: QuadNumber;
  algebraic: number;
  /** Dimension of the eigenspace. */
  geometric: number;
  vectors: QuadNumber[][];
  /** Rational eigenvalues: A − λI, for display. */
  shifted: Matrix | null;
}

interface SolutionBase {
  size: number;
  charPoly: Poly;
  factorization: Factorization;
  trace: Rational;
  determinant: Rational;
  symmetric: boolean;
}

export interface ExactEigen extends SolutionBase {
  mode: "exact";
  eigenvalues: ExactEigenvalue[];
  diagonalizable: boolean;
  /** For all-rational diagonalizable matrices: P (eigenvector columns) and D. */
  diagonalization: { p: Matrix; d: Matrix } | null;
}

export interface NumericEigenvalue {
  value: Complex;
  vector: Complex[];
}

export interface NumericEigen extends SolutionBase {
  mode: "numeric";
  eigenvalues: NumericEigenvalue[];
  /** Imaginary parts below this share of an eigenvalue's size are reported as 0. */
  tolerance: number;
}

export type EigenSolution = ExactEigen | NumericEigen;

function isSymmetric(a: Matrix): boolean {
  return a.every((row, i) =>
    row.every(
      (value, j) => value.num === a[j][i].num && value.den === a[j][i].den
    )
  );
}

export function subtractLambda(a: Matrix, lambda: Rational): Matrix {
  return a.map((row, i) =>
    row.map((value, j) => (i === j ? addR(value, negR(lambda)) : value))
  );
}

function rationalEigenvalue(a: Matrix, root: RationalRoot): ExactEigenvalue {
  const shifted = subtractLambda(a, root.root);
  const basis = nullSpace(shifted).map(toIntegerVector);
  return {
    value: quadFromRational(root.root),
    algebraic: root.multiplicity,
    geometric: basis.length,
    vectors: basis.map((v) => v.map(quadFromRational)),
    shifted,
  };
}

function quadraticEigenvalues(
  a: Matrix,
  quadratic: Poly
): ExactEigenvalue[] | null {
  const solution = solveQuadratic(quadratic[2], quadratic[1], quadratic[0]);
  if (solution === null || solution.surd === null) {
    return null;
  }
  const { p, q, m, s, complex } = solution.surd;
  const real = makeRational(p, s);
  const imaginary = makeRational(q, s);
  const d = complex ? -m : m;
  return [1, -1].map((sign) => {
    const value: QuadNumber = {
      a: real,
      b: sign === 1 ? imaginary : negR(imaginary),
      d,
    };
    const vectors = nullSpaceQuad(shiftMatrix(a, value));
    return {
      value,
      algebraic: 1,
      geometric: vectors.length,
      vectors,
      shifted: null,
    };
  });
}

function diagonalizationOf(eigenvalues: ExactEigenvalue[], n: number) {
  if (!eigenvalues.every((e) => e.shifted !== null)) {
    return null;
  }
  const columns: Rational[][] = [];
  const diagonal: Rational[] = [];
  for (const e of eigenvalues) {
    for (const vector of e.vectors) {
      columns.push(vector.map((x) => x.a));
      diagonal.push(e.value.a);
    }
  }
  if (columns.length !== n) {
    return null;
  }
  const p = Array.from({ length: n }, (_, i) => columns.map((col) => col[i]));
  const d = Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (_, j) => (i === j ? diagonal[i] : R_ZERO))
  );
  return { p, d };
}

const CLUSTER = 1e-6;

function numericSolution(a: Matrix, base: SolutionBase): NumericEigen {
  const floats = a.map((row) => row.map(toNumberR));
  const values = numericEigenvalues(floats);
  const found: NumericEigenvalue[] = [];
  for (const [index, value] of values.entries()) {
    const same = found
      .filter(
        (e) =>
          Math.hypot(e.value.re - value.re, e.value.im - value.im) < CLUSTER
      )
      .map((e) => e.vector);
    const vector = numericEigenvector(floats, value, same, same.length + index);
    found.push({ value, vector });
  }
  return {
    mode: "numeric",
    ...base,
    eigenvalues: found,
    tolerance: REAL_TOLERANCE,
  };
}

export function solveEigen(a: Matrix): EigenSolution {
  const n = a.length;
  const charPoly = characteristicPolynomial(a);
  const factorization = factorRationalRoots(charPoly);
  const base: SolutionBase = {
    size: n,
    charPoly,
    factorization,
    trace: a.reduce((sum, row, i) => addR(sum, row[i]), R_ZERO),
    // det(λI − A) at λ = 0 is (−1)ⁿ det A.
    determinant: n % 2 === 0 ? charPoly[0] : negR(charPoly[0]),
    symmetric: isSymmetric(a),
  };
  const { remainder, roots, searched } = factorization;
  if (searched && remainder.length <= 3) {
    const quadratic =
      remainder.length === 3 ? quadraticEigenvalues(a, remainder) : [];
    if (quadratic !== null) {
      const eigenvalues = [
        ...roots.map((root) => rationalEigenvalue(a, root)),
        ...quadratic,
      ];
      const diagonalizable = eigenvalues.every(
        (e) => e.geometric === e.algebraic
      );
      return {
        mode: "exact",
        ...base,
        eigenvalues,
        diagonalizable,
        diagonalization: diagonalizable
          ? diagonalizationOf(eigenvalues, n)
          : null,
      };
    }
  }
  return numericSolution(a, base);
}
